import { Button, Card, CardContent, IconButton, InputAdornment, TextField, Alert, AlertTitle, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {FormEvent, useState, useEffect, Suspense, lazy} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ApiError, financeApi } from "../lib/api";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { queryKeys } from "../lib/queryKeys";

const CurrencySmokePanel = lazy(() => import("../features/debug/CurrencySmokePanel").then((module) => ({ default: module.CurrencySmokePanel })));

const selectedGroupStorageKey = "financeSelectedGroupId";

function inviteExpiration(expiresAt: string) {
  const expires = new Date(expiresAt);
  if (Number.isNaN(expires.getTime())) return "Expiration unknown";
  return expires.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

type RegisterStep = "details" | "verify";

export function InviteRegisterPage() {
  const { code = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [authToken, setAuthToken] = useState(() => localStorage.getItem("financeToken") || "");
  const [mode, setMode] = useState<"login" | "register">("login");
  const inviteQuery = useQuery({ queryKey: queryKeys.invite(code), queryFn: () => financeApi.invite(code), retry: false, enabled: Boolean(code) });
  const meQuery = useQuery({ queryKey: ["me", authToken || "cookie"], queryFn: financeApi.me, retry: false });
  const [status, setStatus] = useState("");
  const [registerStatus, setRegisterStatus] = useState("");
  const [registerStep, setRegisterStep] = useState<RegisterStep>("details");
  const [registerEmail, setRegisterEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  const loginMutation = useMutation({
    mutationFn: (payload: { email: string; password: string }) => financeApi.login(payload),
    onSuccess: (result) => {
      localStorage.setItem("financeToken", result.access_token);
      setAuthToken(result.access_token);
      setStatus("");
      void queryClient.invalidateQueries({ queryKey: queryKeys.me });
    },
    onError: (error) => {
      localStorage.removeItem("financeToken");
      setAuthToken("");
      setStatus(error instanceof Error ? error.message : "Sign in failed");
      void queryClient.invalidateQueries({ queryKey: queryKeys.me });
    },
  });

  const registerMutation = useMutation({
    mutationFn: (payload: { email: string; password: string; display_name: string; invite_code: string }) => financeApi.registerWithInvite(payload),
    onSuccess: (_result, variables) => {
      setRegisterStep("verify");
      setRegisterEmail(variables.email);
      // Fetch initial cooldown
      financeApi.getVerificationCooldown(variables.email).then((res) => setCooldownSeconds(res.seconds_remaining)).catch(() => {});
    },
    onError: (error) => setRegisterStatus(error instanceof Error ? error.message : "Registration failed"),
  });

  const sendCodeMutation = useMutation({
    mutationFn: (email: string) => financeApi.sendVerificationCode(email),
    onSuccess: (result) => {
      setRegisterStatus(result.message);
      financeApi.getVerificationCooldown(registerEmail).then((res) => setCooldownSeconds(res.seconds_remaining)).catch(() => {});
    },
    onError: (error) => setRegisterStatus(error instanceof Error ? error.message : "Failed to send code"),
  });

  const verifyCodeMutation = useMutation({
    mutationFn: ({ email, code }: { email: string; code: string }) => financeApi.verifyCode(email, code),
    onSuccess: async (result) => {
      if (result.verified) {
        localStorage.setItem("financeToken", result.access_token);
        setAuthToken(result.access_token);
        setRegisterStatus("");
        await queryClient.invalidateQueries();
        navigate("/home", { replace: true });
      } else {
        setRegisterStatus(result.message);
      }
    },
    onError: (error) => setRegisterStatus(error instanceof Error ? error.message : "Verification failed"),
  });

  const acceptMutation = useMutation({
    mutationFn: () => financeApi.acceptInvite(code),
    onSuccess: (group) => {
      localStorage.setItem(selectedGroupStorageKey, group.id);
      void queryClient.invalidateQueries();
      navigate("/home", { replace: true });
    },
    onError: (error) => setStatus(error instanceof Error ? error.message : "Could not accept invite"),
  });

  const submitLogin = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus("");
    const data = new FormData(event.currentTarget);
    loginMutation.mutate({
      email: String(data.get("email") || "").trim(),
      password: String(data.get("password") || ""),
    });
  };

  const submitRegisterDetails = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setRegisterStatus("");
    const data = new FormData(event.currentTarget);
    const email = String(data.get("email") || "").trim();
    const password = String(data.get("password") || "");
    const displayName = String(data.get("display_name") || "").trim();
    registerMutation.mutate({ email, password, display_name: displayName, invite_code: code });
    return;
  };

  const handleSendCode = () => {
    if (registerEmail && cooldownSeconds === 0) {
      sendCodeMutation.mutate(registerEmail);
    }
  };

  const handleVerifyCode = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setRegisterStatus("");
    if (verificationCode.length === 6) {
      verifyCodeMutation.mutate({ email: registerEmail, code: verificationCode });
    }
    return;
  };

  // Tick the resend cooldown down to zero once it has been set (after register or resend).
  useEffect(() => {
    if (cooldownSeconds <= 0) return;
    const id = setTimeout(() => setCooldownSeconds((seconds) => seconds - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldownSeconds]);

  const inviteExpired =
    inviteQuery.data?.is_expired ||
    (inviteQuery.error instanceof ApiError && inviteQuery.error.status === 410);

  // Disable send button for test emails (they don't receive code, but still need to enter debug code)

  return (
    <section className="auth-shell invite-auth-shell">
      <Card className="auth-card invite-auth-card">
        <Suspense fallback={null}>
          <CurrencySmokePanel
            subtitle="Sign in or create an account to connect to a shared workspace"
          />
        </Suspense>
        <CardContent>
          <h2>Join group</h2>
          {inviteQuery.isLoading ? <p>Loading invite...</p> : null}
          {inviteExpired ? (
            <div className="invite-expired-state">
              <p>Invitation expired</p>
              <Button className="primary-link" component={Link} to="/">
                Open login
              </Button>
            </div>
          ) : null}
          {inviteQuery.isError && !inviteExpired ? (
            <>
              <p>This invite link is unavailable.</p>
              <Button className="primary-link" component={Link} to="/">
                Open login
              </Button>
            </>
          ) : null}
          {inviteQuery.data && !inviteExpired ? (
            <>
              <div className="invite-auth-summary">
                <strong>
                  {inviteQuery.data.created_by_display_name} invited you to {inviteQuery.data.family_name}
                </strong>
                <span>Role: {inviteQuery.data.role}</span>
                <span>Valid until {inviteExpiration(inviteQuery.data.expires_at)}</span>
                <span>
                  {inviteQuery.data.used_count} of {inviteQuery.data.max_uses} activations used
                </span>
              </div>
              {meQuery.data ? (
                <div className="invite-auth-form">
                  <p>Signed in as {meQuery.data.display_name} · {meQuery.data.email}</p>
                  <p className="auth-status" role="status">
                    {status}
                  </p>
                  <Button
                    className="primary-link"
                    type="button"
                    onClick={() => acceptMutation.mutate()}
                    disabled={acceptMutation.isPending}
                  >
                    Accept invite
                  </Button>
                </div>
              ) : (
                <>
                  <div className="invite-auth-tabs" role="tablist" aria-label="Invite authentication">
                    <Button
                      className={mode === "login" ? "active" : ""}
                      type="button"
                      onClick={() => {
                        setMode("login");
                        setStatus("");
                        setRegisterStep("details");
                        setRegisterStatus("");
                      }}
                    >
                      Sign in
                    </Button>
                    <Button
                      className={mode === "register" ? "active" : ""}
                      type="button"
                      onClick={() => {
                        setMode("register");
                        setStatus("");
                        setRegisterStep("details");
                        setRegisterStatus("");
                        setRegisterEmail("");
                        setVerificationCode("");
                        setCooldownSeconds(0);
                      }}
                    >
                      Create account
                    </Button>
                  </div>
                  {mode === "login" ? (
                    <form className="react-auth-form invite-auth-form" onSubmit={submitLogin}>
                      <TextField
                        className="react-auth-input"
                        label="Email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                      />
                      <TextField
                        className="react-auth-input"
                        label="Password"
                        name="password"
                        type={showLoginPassword ? "text" : "password"}
                        autoComplete="current-password"
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  edge="end"
                                  type="button"
                                  aria-label={showLoginPassword ? "Hide password" : "Show password"}
                                  title={showLoginPassword ? "Hide password" : "Show password"}
                                  onClick={() => setShowLoginPassword((current) => !current)}
                                >
                                  {showLoginPassword ? <VisibilityOffOutlinedIcon aria-hidden="true" /> : <VisibilityOutlinedIcon aria-hidden="true" />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          },
                        }}
                        required
                      />
                      <p className="auth-status" role="status">
                        {status}
                      </p>
                      <Button className="primary-link" type="submit" disabled={loginMutation.isPending}>
                        Continue
                      </Button>
                    </form>
                  ) : (
                    <>
                      {registerStep === "details" && (
                        <form className="react-auth-form invite-auth-form" onSubmit={submitRegisterDetails}>
                          <TextField
                            className="react-auth-input"
                            label="Display name"
                            name="display_name"
                            autoComplete="name"
                            required
                          />
                          <TextField
                            className="react-auth-input"
                            label="Email"
                            name="email"
                            type="email"
                            autoComplete="email"
                            required
                          />
                          <TextField
                            className="react-auth-input"
                            label="Password"
                            name="password"
                            type={showRegisterPassword ? "text" : "password"}
                            slotProps={{
                              htmlInput: { minLength: 8 },
                              input: {
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      edge="end"
                                      type="button"
                                      aria-label={showRegisterPassword ? "Hide new password" : "Show new password"}
                                      title={showRegisterPassword ? "Hide password" : "Show password"}
                                      onClick={() => setShowRegisterPassword((current) => !current)}
                                    >
                                      {showRegisterPassword ? <VisibilityOffOutlinedIcon aria-hidden="true" /> : <VisibilityOutlinedIcon aria-hidden="true" />}
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              },
                            }}
                            autoComplete="new-password"
                            required
                          />
                          {registerStatus && (
                            <Alert severity={registerMutation.isError ? "error" : "info"} sx={{ mb: 2 }}>
                              <AlertTitle>{registerMutation.isError ? "Error" : "Info"}</AlertTitle>
                              {registerStatus}
                            </Alert>
                          )}
                          <Button
                            className="primary-link"
                            type="submit"
                            disabled={registerMutation.isPending}
                          >
                            {registerMutation.isPending ? "Creating account..." : "Create account"}
                          </Button>
                        </form>
                      )}
                      {registerStep === "verify" && (
                        <div className="react-auth-form invite-auth-form">
                          <Typography variant="body1" gutterBottom>
                            Enter the 6-digit code sent to <strong>{registerEmail}</strong>
                          </Typography>
                          <form className="react-auth-form" style={{ marginTop: 0 }} onSubmit={handleVerifyCode}>
                            <TextField
                              className="react-auth-input"
                              label="Verification code"
                              name="verification_code"
                              type="text"
                              inputMode="numeric"
                              value={verificationCode}
                              onChange={(e) => setVerificationCode(e.target.value.slice(0, 6))}
                              autoComplete="one-time-code"
                              required
                              autoFocus
                            />
                            {registerStatus && (
                              <Alert severity={verifyCodeMutation.isError ? "error" : "info"} sx={{ mb: 2 }}>
                                <AlertTitle>{verifyCodeMutation.isError ? "Error" : "Info"}</AlertTitle>
                                {registerStatus}
                              </Alert>
                            )}
                            <Button
                              className="primary-link"
                              type="submit"
                              disabled={verifyCodeMutation.isPending || verificationCode.length !== 6}
                            >
                              {verifyCodeMutation.isPending ? "Verifying..." : "Verify code"}
                            </Button>
                          </form>
                          <div className="resend-code-section" style={{ marginTop: 16 }}>
                            {cooldownSeconds > 0 ? (
                              <Typography variant="body2" color="text.secondary">
                                Resend code in {cooldownSeconds}s
                              </Typography>
                            ) : (
                              <Button
                                size="small"
                                onClick={handleSendCode}
                                disabled={sendCodeMutation.isPending}
                                startIcon={sendCodeMutation.isPending ? "⏳" : undefined}
                              >
                                {sendCodeMutation.isPending ? "Sending..." : "Resend code"}
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          ) : null}
        </CardContent>
      </Card>
    </section>
  );
}
